import { useState, FormEvent } from 'react';
import type { Outlet, CreateOutletRequest, Product } from '@photobooth/types';

interface OutletFormProps {
  outlet?: Outlet;
  products: Product[];
  onSubmit: (data: CreateOutletRequest) => Promise<void>;
  onCancel: () => void;
}

export function OutletForm({ outlet, products, onSubmit, onCancel }: OutletFormProps) {
  const [formData, setFormData] = useState({
    name: outlet?.name || '',
    location: outlet?.location || '',
    address: outlet?.address || '',
    mapsUrl: outlet?.mapsUrl || '',
    syncInterval: outlet?.configuration.syncInterval || 30,
    isPublic: outlet?.isPublic ?? true,
    productIds: outlet?.productIds ?? [] as string[],
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      const data: CreateOutletRequest = {
        name: formData.name,
        location: formData.location,
        address: formData.address || undefined,
        mapsUrl: formData.mapsUrl || undefined,
        isPublic: formData.isPublic,
        productIds: formData.productIds.length > 0 ? formData.productIds : undefined,
        configuration: {
          syncInterval: formData.syncInterval,
        },
      };

      await onSubmit(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save outlet');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      <div>
        <label htmlFor="name" className="block text-sm font-medium text-text mb-1">
          Outlet Name *
        </label>
        <input
          id="name"
          type="text"
          required
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          placeholder="Downtown Branch"
        />
      </div>

      <div>
        <label htmlFor="location" className="block text-sm font-medium text-text mb-1">
          Location *
        </label>
        <input
          id="location"
          type="text"
          required
          value={formData.location}
          onChange={(e) => setFormData({ ...formData, location: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          placeholder="Jakarta"
        />
      </div>

      <div>
        <label htmlFor="address" className="block text-sm font-medium text-text mb-1">
          Address
        </label>
        <textarea
          id="address"
          value={formData.address}
          onChange={(e) => setFormData({ ...formData, address: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          placeholder="Full address (optional)"
          rows={3}
        />
      </div>

      <div>
        <label htmlFor="mapsUrl" className="block text-sm font-medium text-text mb-1">
          Google Maps URL
        </label>
        <input
          id="mapsUrl"
          type="url"
          value={formData.mapsUrl}
          onChange={(e) => setFormData({ ...formData, mapsUrl: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          placeholder="https://maps.google.com/..."
        />
        <p className="text-sm text-text-muted mt-1">
          Link for &quot;View on map&quot; on the public outlets page
        </p>
      </div>

      <div>
        <label htmlFor="syncInterval" className="block text-sm font-medium text-text mb-1">
          Sync Interval (minutes) *
        </label>
        <input
          id="syncInterval"
          type="number"
          required
          min="5"
          max="120"
          value={formData.syncInterval}
          onChange={(e) => setFormData({ ...formData, syncInterval: parseInt(e.target.value) })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
        />
        <p className="text-sm text-text-muted mt-1">
          How often the booth app syncs with the server
        </p>
      </div>

      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="isPublic"
          checked={formData.isPublic}
          onChange={(e) => setFormData({ ...formData, isPublic: e.target.checked })}
          className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-2 focus:ring-primary"
        />
        <label htmlFor="isPublic" className="text-sm font-medium text-text">
          Available on website
        </label>
      </div>

      <div>
        <label className="block text-sm font-medium text-text mb-2">
          Products available at this outlet
        </label>
        <div className="border border-gray-300 rounded-md p-3 max-h-48 overflow-y-auto space-y-2">
          {products.length === 0 ? (
            <p className="text-sm text-text-muted">No products available</p>
          ) : (
            products.map((product) => (
              <label
                key={product.productId}
                className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={formData.productIds.includes(product.productId)}
                  onChange={() => {
                    setFormData((prev) => ({
                      ...prev,
                      productIds: prev.productIds.includes(product.productId)
                        ? prev.productIds.filter((id) => id !== product.productId)
                        : [...prev.productIds, product.productId],
                    }));
                  }}
                  className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-2 focus:ring-primary"
                />
                <span className="text-sm text-text">{product.name}</span>
              </label>
            ))
          )}
        </div>
      </div>

      <div className="flex gap-3 pt-4">
        <button
          type="submit"
          disabled={isSubmitting}
          className="flex-1 bg-primary text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:opacity-50"
        >
          {isSubmitting ? 'Saving...' : outlet ? 'Update Outlet' : 'Create Outlet'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 bg-gray-200 text-text py-2 px-4 rounded-md hover:bg-gray-300"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
